import { useMemo } from 'react';
import { ColumnDescription } from 'react-bootstrap-table-next';
import { Tooltip, Stack } from '@mui/material';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { ItemExtended } from '~services/item/types';
import { useAppSelector } from '~hooks/useAppSelector';
import { useUtcOffset } from '~hooks/useUtcOffset';
import { selectAuth } from '~store/slices/auth';
import {
  OrderClientInfo,
  OrderMFlexQR,
  OrderRouteInfo,
  VehicleInfo,
  UserInfo,
  SkillsListInfo,
  ExpirationDateInfo,
  CapabilitiesInfo,
  SourceImportTypeInfo,
  OrderServiceTypeInfo,
  LastOrderEventInfo,
  LogisiticZoneInfo,
  OrderFinalizedStateChip,
  OrderRevisitChip,
} from '~components/index';
import { isCurrentLoggedCompany } from '~utils/partnerCompany';
import { formatCurrencyValue, formatterDate } from '~utils/formatter';
import { getFeatureAvailableToDate } from '~utils/feature';
import { getStopRevisitInfo } from '~utils/route';

import DashboardVariantV2CarrierContentFinalizedTabDataTableActionsMenu, {
  DashboardVariantV2CarrierContentFinalizedTabDataTableActionsMenuProps,
} from './ActionsMenu';

type UseFinalizedTabDataTableColumnsParams = Omit<
  DashboardVariantV2CarrierContentFinalizedTabDataTableActionsMenuProps,
  'data'
>;

export const useFinalizedTabDataTableColumns = ({
  loadingDowloadEvidencesOrderPDF,
  onDowloadEvidencesOrderPDF,
}: UseFinalizedTabDataTableColumnsParams): ColumnDescription<ItemExtended>[] => {
  const { user } = useAppSelector(selectAuth);

  const formatterDateUtcOffset = useUtcOffset();

  const columns = useMemo<ColumnDescription<ItemExtended>[]>(
    () => [
      {
        dataField: 'title',
        text: 'Nombre de contacto',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          let isRevisit = false;

          if (row.routeItemStateTypeId !== null) {
            isRevisit = getStopRevisitInfo(
              row.routeItemStateTypeId,
              row.visitCount,
            ).isRevisit;
          }

          return (
            <Stack spacing={0.5} alignItems="flex-start">
              {isRevisit && <OrderRevisitChip size="small" />}

              <OrderClientInfo
                name={row.title}
                itemTypeId={row.itemTypeId}
                trackingCode={row.trackingCode}
                duplicated={row.duplicated}
              />
            </Stack>
          );
        },
        headerStyle: { width: 180 },
        sort: true,
      },
      {
        dataField: 'address',
        text: 'Dirección',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        headerStyle: { width: 300 },
        sort: true,
      },
      {
        dataField: 'addressNotes',
        text: 'Notas de dirección',
        classes: 'truncated-two-lines',
        formatter: (cell) => {
          if (!cell) return null;

          return (
            <Tooltip title={cell} placement="bottom-start">
              <span style={{ maxLines: 1 }}>{cell}</span>
            </Tooltip>
          );
        },
        hidden: true,
        headerStyle: { width: 200 },
        sort: true,
      },
      {
        dataField: 'referenceId',
        text: 'Nro. de Orden',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.referenceId) return null;

          return (
            <Tooltip title={row.referenceId} placement="bottom-start">
              <span>{row.referenceId}</span>
            </Tooltip>
          );
        },
        headerStyle: { width: 120 },
        sort: true,
      },
      {
        dataField: 'qrcode',
        text: 'QR',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.qrcode) return null;

          return (
            <OrderMFlexQR.IconWithTooltipInfo
              qrData={row.qrcode}
              referenceId={row.referenceId}
            />
          );
        },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitPaid,
        },
        headerAlign: 'center',
        align: 'center',
        hidden: true,
        headerStyle: { width: 60 },
      },
      {
        dataField: 'tags',
        text: 'Grupo',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        headerStyle: { width: 120 },
      },
      {
        dataField: 'routeCode',
        text: 'Ruta',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.routeCode) return null;

          return (
            <OrderRouteInfo
              apiTransactionId={row.apiTransactionId as string}
              routeId={row.routeId as string}
              routeCode={row.routeCode}
              carrierCompanyId={row.carrierCompanyId}
              carrierCompanyName={row.carrierCompanyName}
            />
          );
        },
        headerStyle: { width: 120 },
      },
      {
        dataField: 'vehicle',
        text: 'Vehículo',
        formatter: (_cell, row) => {
          if (!row.routeCode) return null;

          if (!row.vehicle) return null;

          return (
            <VehicleInfo vehicle={row.vehicle.name} color={row.vehicle.color} />
          );
        },
        hidden: true,
        headerStyle: { width: 120 },
      },
      {
        dataField: 'driver',
        text: 'Chofer',
        formatter: (_cell, row) => {
          if (!row.routeCode) return null;

          let driverName = 'Sin asignar';
          let urlAvatar: string | undefined = undefined;

          if (row.driver) {
            driverName = `${row.driver?.lastName}, ${row.driver?.firstName}`;
            urlAvatar = row.driver.avatarFile?.url;
          }

          return <UserInfo name={driverName} urlAvatar={urlAvatar} />;
        },
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'skillsNeeded',
        text: 'Cargas especiales',
        classes: 'truncated-two-lines',
        formatter: (cell) => <SkillsListInfo skills={cell} />,
        align: 'center',
        headerStyle: { width: 100 },
      },
      {
        dataField: 'realArrivalDateTime',
        text: 'Entrega',
        classes: 'truncated-two-lines',
        formatter: (cell) => formatterDateUtcOffset(cell, 'DD/MM/YYYY HH:mm'),
        headerStyle: { width: 160 },
      },
      {
        dataField: 'carrierCompanyName',
        text: 'Empresa',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <span>
            {isCurrentLoggedCompany(
              row.carrierCompanyId,
              user?.companyId ?? null,
            )
              ? row.giverCompanyName
              : row.carrierCompanyName}
          </span>
        ),
        hidden: true,
        headerStyle: { width: 150 },
      },
      {
        dataField: 'creationDateTime',
        text: 'Fecha de creación',
        classes: 'truncated-two-lines',
        formatter: (cell) => formatterDate(cell, { format: 'DD/MM/YYYY' }),
        hidden: true,
        headerStyle: { width: 150 },
        sort: true,
      },
      {
        dataField: 'maxDeliveryDateTime',
        text: 'Fecha de vencimiento',
        classes: 'truncated-two-lines',
        formatter: (cell) => <ExpirationDateInfo expirationDate={cell} />,
        hidden: true,
        headerStyle: { width: 150 },
        sort: true,
      },
      {
        dataField: 'contactName',
        text: 'Datos de contacto',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <Stack>
            <span>{row.contactName}</span>
            <span>{row.contactIdentification}</span>
          </Stack>
        ),
        hidden: true,
        headerStyle: { width: 200 },
        sort: true,
      },
      {
        dataField: 'contactEmail',
        text: 'Correo electrónico',
        classes: 'truncated-two-lines',
        hidden: true,
        headerStyle: { width: 220 },
        sort: true,
      },
      {
        dataField: 'contactPhone',
        text: 'Telefono',
        classes: 'truncated-two-lines',
        hidden: true,
        headerStyle: { width: 200 },
        sort: true,
      },
      {
        dataField: 'contactMobile',
        text: 'Celular / Whatsapp',
        classes: 'truncated-two-lines',
        hidden: true,
        headerStyle: { width: 200 },
        sort: true,
      },
      {
        dataField: 'unit1',
        text: 'Capacidades',
        formatter: (_cell, row) => (
          <CapabilitiesInfo
            capacity1={row.unit1 * row.amount}
            capacity2={row.unit2 * row.amount}
            numberOfPackages={row.numberOfPackages}
            timeWindow={{
              allDay: row.timeWindowAllDay,
              timeFrom: row.timeWindowFrom1,
              timeTo: row.timeWindowTo1,
              timeFrom2: row.timeWindowFrom2,
              timeTo2: row.timeWindowTo2,
            }}
            maximumVisits={null}
            fontSize={12}
            fontWeight="bold"
          />
        ),
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'price',
        text: 'Precio',
        // classes: 'truncated-two-lines',
        formatter: (cell) => formatCurrencyValue(cell, { style: 'decimal' }),
        hidden: true,
        headerStyle: { width: 160 },
        sort: true,
      },
      {
        dataField: 'customField1',
        text: 'Campo personalizado 1',
        hidden: true,
        headerStyle: { width: 160 },
        sort: true,
      },
      {
        dataField: 'customField2',
        text: 'Campo personalizado 2',
        hidden: true,
        headerStyle: { width: 160 },
        sort: true,
      },
      {
        dataField: 'customField3',
        text: 'Campo personalizado 3',
        hidden: true,
        headerStyle: { width: 160 },
        sort: true,
      },
      {
        dataField: 'quantityArticles',
        text: 'Cantidad de artículos',
        classes: 'truncated-two-lines',
        hidden: true,
        headerAlign: 'center',
        align: 'center',
        headerStyle: { width: 90 },
      },
      {
        dataField: 'store',
        classes: 'truncated-two-lines',
        text: 'Tienda',
        hidden: true,
        headerStyle: { width: 160 },
        sort: true,
      },
      {
        dataField: 'zipCode',
        classes: 'truncated-two-lines',
        text: 'Código postal',
        hidden: true,
        headerStyle: { width: 90 },
        sort: true,
      },
      {
        dataField: 'sourceImportTypeId',
        text: 'Medio de importación',
        classes: 'truncated-two-lines',
        hidden: true,
        formatter: (_cell, row) => (
          <SourceImportTypeInfo sourceImportTypeId={row.sourceImportTypeId} />
        ),
        headerStyle: { width: 120 },
        sort: true,
      },
      {
        dataField: 'externalStatusType',
        isDummyField: true,
        text: 'Estado Flex',
        classes: 'truncated-two-lines',
        hidden: true,
        formatter: (_cell, row) => {
          if (!row.externalStatusType) return null;

          return (
            <Tooltip
              title={row.externalStatusType.description}
              placement="bottom-start"
            >
              <span>{row.externalStatusType.description}</span>
            </Tooltip>
          );
        },
        headerStyle: { width: 180 },
      },
      {
        dataField: 'lastRejectedReason',
        text: 'Motivo de revisita',
        classes: 'truncated-two-lines',
        formatter: (cell) => {
          if (!cell) return null;

          return (
            <Tooltip title={cell} placement="bottom-start">
              <span style={{ maxLines: 1 }}>{cell}</span>
            </Tooltip>
          );
        },
        hidden: true,
        headerStyle: { width: 220 },
        sort: true,
      },
      {
        dataField: 'routeItemFeedbackNotes',
        text: 'Comentarios de la visita',
        classes: 'truncated-two-lines',
        formatter: (cell) => (
          <Tooltip title={cell} placement="bottom-start">
            <span style={{ maxLines: 1 }}>{cell}</span>
          </Tooltip>
        ),
        hidden: true,
        headerStyle: { width: 220 },
      },
      {
        dataField: 'serviceType',
        text: 'Tipo de servicio',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <OrderServiceTypeInfo serviceType={row.serviceType} />
        ),
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitNotPro,
        },
        hidden: true,
        headerStyle: { width: 210 },
        sort: true,
      },
      {
        dataField: 'itemHistory',
        isDummyField: true,
        text: 'Último evento',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.lastItemStory) return null;

          return (
            <LastOrderEventInfo
              description={row.lastItemStoryType?.description ?? ''}
              user={{
                // eslint-disable-next-line max-len
                name: `${row?.lastItemStory?.user?.lastName}, ${row?.lastItemStory?.user?.firstName}`,
                urlAvatar: row?.lastItemStory?.user?.avatarUrl || undefined,
              }}
            />
          );
        },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitPaid,
        },
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'assignedUser',
        isDummyField: true,
        text: 'Usuario asignado',
        formatter: (_cell, row) => {
          let userName = 'Sin asignar';
          let userUrlAvatar: string | undefined = undefined;

          if (row.assignedUser) {
            userName = `${row.assignedUser?.lastName}, ${row.assignedUser?.firstName}`;
            userUrlAvatar = row.assignedUser.avatarUrl || undefined;
          }

          return <UserInfo name={userName} urlAvatar={userUrlAvatar} />;
        },
        hidden: true,
        headerStyle: { width: 160 },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitNotPro,
        },
        sort: true,
      },
      {
        dataField: 'shipPrice',
        isDummyField: true,
        text: 'Tarifa',
        // classes: 'truncated-two-lines',
        formatter: (_cell, row) =>
          row.shipPrice !== null
            ? formatCurrencyValue(row.shipPrice, { style: 'decimal' })
            : 'N/A',
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmBillingGiver,
          availableToDate: getFeatureAvailableToDate(
            PlanFeaturesTypes.MgmBillingGiver,
          ),
        },
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'zoneId',
        text: 'Zona logística',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.zone) return 'Sin asignar';

          return (
            <LogisiticZoneInfo name={row.zone.code} color={row.zone.color} />
          );
        },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmZones,
        },
        hidden: true,
        headerStyle: { width: 200 },
        sort: true,
      },
      {
        dataField: 'itemStateTypeId',
        text: 'Estado',
        classes: 'truncated-two-lines',
        formatter: (cell) => <OrderFinalizedStateChip stateId={cell} />,
        headerAlign: 'center',
        align: 'center',
        headerStyle: { width: 170 },
      },
      {
        dataField: 'actions',
        isDummyField: true,
        text: '',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <DashboardVariantV2CarrierContentFinalizedTabDataTableActionsMenu
            data={row}
            loadingDowloadEvidencesOrderPDF={loadingDowloadEvidencesOrderPDF}
            onDowloadEvidencesOrderPDF={onDowloadEvidencesOrderPDF}
          />
        ),
        headerStyle: { width: 60 },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.companyId, loadingDowloadEvidencesOrderPDF],
  );

  return columns;
};
